import React, { useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import Lottie from 'react-lottie'
import { Label } from '@/components/molecules/label'
import { Button } from '@/components/atoms/button'
import {
  modalContainerStyle,
  modalWrapperStyle,
  modalStyle,
  modalTitleStyle,
  modalSectionStyle,
  modalItemWrapperStyle,
  modalFooterStyle,
} from '@/components/pages/setting/guest-room/room/_room/modal/modal-layout'
import { createSalesSubjectSet, updateSalesSubjectSet } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SalesSubjectSetType, SalesSubjectMasterType } from '@/models/sales-manager/sales'
import { DeleteIcon } from '@/components/molecules/settings/icon/delete-icon'
import { SubjectSelect } from '@/components/molecules/customer/sales-manager/usage-detail/subject-select'
import uuid from 'react-uuid'

type Props = {
  nextDisplayOrder: number
  editSubjectSet?: SalesSubjectSetType
  salesSubjectMasters: SalesSubjectMasterType[]
  onClickCancel: () => void
  onSuccess: () => void
}

type SubjectSelectValue = {
  salesSubjectId: string
  salesSubjectName?: string
  salesSubSubjectId: string
  salesSubSubjectName?: string
}

export const SubjectSetModal: React.FC<Props> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<{
    salesSubjectSetNameId: number
    salesSubjectSetName: string
    salesSubjectSetMap: {
      salesSubjectSetMapId: string
      salesSubjectMasterId: string
      salesSubjectMasterName?: string
      salesSubSubjectId: string
      salesSubSubjectName?: string
    }[]
  }>({
    defaultValues: props.editSubjectSet
      ? props.editSubjectSet
      : {
          salesSubjectSetNameId: props.nextDisplayOrder,
          salesSubjectSetMap: [
            { salesSubjectSetMapId: uuid(), salesSubjectMasterId: '', salesSubSubjectId: '' },
            { salesSubjectSetMapId: uuid(), salesSubjectMasterId: '', salesSubSubjectId: '' },
            { salesSubjectSetMapId: uuid(), salesSubjectMasterId: '', salesSubSubjectId: '' },
            { salesSubjectSetMapId: uuid(), salesSubjectMasterId: '', salesSubSubjectId: '' },
            { salesSubjectSetMapId: uuid(), salesSubjectMasterId: '', salesSubSubjectId: '' },
          ],
        },
  })
  const {
    fields: salesSubjectSetMap,
    append: appendSubjectSelect,
    remove: removeSubjectSelect,
  } = useFieldArray({
    control,
    name: 'salesSubjectSetMap',
    rules: {
      validate: () => getValues('salesSubjectSetMap').filter(i => i.salesSubjectMasterId).length > 0,
    },
  })
  const [loading, setLoading] = useState<boolean>(false)
  // useFieldArray の値同期で defaultValue が欠けると SubjectSelect がクリアされるため、行ID単位で選択を保持する
  const [selectedByFieldId, setSelectedByFieldId] = useState<Record<string, SubjectSelectValue>>({})

  const onSubmit = async formData => {
    try {
      setLoading(true)
      const { salesSubjectSetMap, ...data } = formData
      data['salesSubjectSetNameId'] = Number(data['salesSubjectSetNameId'])
      data['salesSubjectMasterIds'] = salesSubjectSetMap.filter(i => i.salesSubjectMasterId).map(i => i.salesSubjectMasterId)
      if (props.editSubjectSet?.salesSubjectSetId) {
        await updateSalesSubjectSet({ ...data, salesSubjectSetId: props.editSubjectSet.salesSubjectSetId })
      } else {
        await createSalesSubjectSet(data)
      }
      props.onSuccess()
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }

  const mappedPropItem = useMemo(() => {
    const _mappedPropItem: Record<string, SubjectSelectValue> = {}
    props.editSubjectSet?.salesSubjectSetMap.forEach(propMapItem => {
      _mappedPropItem[propMapItem.salesSubjectMasterId] = {
        salesSubjectId: propMapItem.salesSubjectMasterId,
        salesSubjectName: propMapItem.salesSubjectMasterName,
        salesSubSubjectId: propMapItem.salesSubSubjectId || '',
        salesSubSubjectName: propMapItem.salesSubSubjectName,
      }
    })
    return _mappedPropItem
  }, [props.editSubjectSet])

  const resolveDefaultValue = (item: {
    id: string
    salesSubjectMasterId: string
    salesSubSubjectId?: string
  }): SubjectSelectValue | undefined => {
    if (selectedByFieldId[item.id]) {
      return selectedByFieldId[item.id]
    }
    if (!item.salesSubjectMasterId) {
      return undefined
    }
    if (mappedPropItem[item.salesSubjectMasterId]) {
      return mappedPropItem[item.salesSubjectMasterId]
    }
    // 編集後の科目はマスタから復元（別行削除時の fields 同期後も選択を維持するため）
    const master = props.salesSubjectMasters.find(m => m.id === item.salesSubjectMasterId)
    if (!master) {
      return undefined
    }
    const sub = item.salesSubSubjectId ? master.subSubjects.find(s => s.id === item.salesSubSubjectId) : undefined
    return {
      salesSubjectId: master.id,
      salesSubjectName: master.subjectName,
      salesSubSubjectId: sub?.id || '',
      salesSubSubjectName: sub?.subSubjectName,
    }
  }

  return (
    // オーバーレイで背面操作を遮断しないと、枠外クリックが一覧行に貫通して科目選択が初期化される
    <div css={[modalContainerStyle, { zIndex: 104, height: '100vh', pointerEvents: 'auto' }]}>
      <div css={[modalWrapperStyle, { height: '90%', pointerEvents: 'auto' }]}>
        <form onSubmit={handleSubmit(onSubmit)} css={[modalStyle, { width: 608, position: 'relative', height: 'auto', margin: 0 }]}>
          <div css={modalTitleStyle}>{t('Add subject set')}</div>
          <div css={modalSectionStyle}>
            <div css={modalItemWrapperStyle}>
              <div css={{ marginBottom: 16 }}>
                <Label label={`${t('Subject set ID (order)')} *`} />
                <input
                  {...register('salesSubjectSetNameId', {
                    required: t('The sales subject set id field is required.'),
                    maxLength: { value: 4, message: t('The sales subject set name id must not have more than 4 digits.') },
                  })}
                  css={inputStyle}
                  placeholder={t('Course set ID (within 4 digits)')}
                  type="number"
                />
                {!!errors.salesSubjectSetNameId && (
                  <div css={{ fontSize: 10, color: 'rgba(255,0,0,0.5)', marginTop: 4 }}>{errors.salesSubjectSetNameId?.message}</div>
                )}
              </div>

              <div css={{ marginBottom: 16 }}>
                <Label label={`${t('Subject set name')} *`} />
                <input
                  {...register('salesSubjectSetName', { required: t('Please enter the subject set name.') })}
                  css={inputStyle}
                  placeholder={t('Subject set name')}
                />
                {!!errors.salesSubjectSetName && (
                  <div css={{ fontSize: 10, color: 'rgba(255,0,0,0.5)', marginTop: 4 }}>{errors.salesSubjectSetName?.message}</div>
                )}
              </div>
              <div>
                <Label label={`${t('Subject')} *`} />
                {salesSubjectSetMap.map((item, index) => {
                  return (
                    <div key={item.id} css={{ marginBottom: 16 }}>
                      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Controller
                          name={`salesSubjectSetMap.${index}.salesSubjectMasterId`}
                          control={control}
                          render={({ field: { onChange } }) => (
                            <SubjectSelect
                              onChange={v => {
                                const masterId = v.subSubject?.salesSubjectMasterId || v.subjectMaster.id
                                const subId = v.subSubject?.id || ''
                                onChange(masterId)
                                setValue(`salesSubjectSetMap.${index}.salesSubSubjectId`, subId)
                                setSelectedByFieldId(prev => ({
                                  ...prev,
                                  [item.id]: {
                                    salesSubjectId: v.subjectMaster.id,
                                    salesSubjectName: v.subjectMaster.subjectName,
                                    salesSubSubjectId: subId,
                                    salesSubSubjectName: v.subSubject?.subSubjectName,
                                  },
                                }))
                              }}
                              defaultValue={resolveDefaultValue(item)}
                              salesSubjectMasters={props.salesSubjectMasters}
                              isEditMode={true}
                            />
                          )}
                        />
                        <DeleteIcon onClick={() => removeSubjectSelect(index)} />
                      </div>
                    </div>
                  )
                })}
                <div
                  css={plusButtonRowStyle}
                  onClick={() =>
                    appendSubjectSelect({
                      salesSubjectSetMapId: uuid(),
                      salesSubjectMasterId: '',
                      salesSubSubjectId: '',
                    })
                  }
                >
                  <img src={require('@/static/images/plus.svg')} alt={t('Add subject')} />
                  <p>{t('Add subject')}</p>
                </div>
              </div>
            </div>
          </div>
          <div css={modalFooterStyle}>
            <Button buttonType={3} width={110} height={38} fontSize={14} marginRight={16} onClick={props.onClickCancel}>
              {t('Cancel')}
            </Button>
            <Button buttonType={1} width={110} height={38} fontSize={14} marginRight={32} type="submit">
              {t('Save')}
            </Button>
          </div>
          {loading && (
            <div css={loaderContainer}>
              <Lottie
                height="80px"
                width="80px"
                options={{ animationData: require('@/static/images/json/loading.json'), autoplay: true, loop: true }}
              />
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

const loaderContainer = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'rgba(0,0,0,0.3)',
  borderRadius: 4,
})

const plusButtonRowStyle = css({
  cursor: 'pointer',
  display: 'flex',
  height: 36,
  backgroundColor: '#FAFAFA',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px dashed #CCCCCC',
  p: {
    lineHeight: '36px',
    color: '#F2A40B',
    fontSize: 12,
    fontWeight: 'bold',
    paddingLeft: 8,
  },
  img: {
    width: 20,
    height: 20,
  },
})

const inputStyle = css({
  width: '100%',
  padding: '9px 16px',
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  fontSize: 14,
  lineHeight: '14px',
  height: 32,
  '::placeholder': { color: '#CCCCCC' },
})
